<script setup lang="ts">
import { computed } from 'vue'
import type { Finding } from '@/lib/usePolledScan'
import { findingTone } from '@/lib/tone'
import ToneChip from './ToneChip.vue'
import RefLink from './RefLink.vue'
import EvidenceTable from './EvidenceTable.vue'

const props = defineProps<{
  finding: Finding
  description?: string
}>()

// Severity is meaningless on a pass, and findingTone already knows that rule.
// Deriving the tone here a second way would open a path around it.
const tone = computed(() => findingTone(props.finding.status, props.finding.severity))
</script>

<template>
  <article
    class="card flex overflow-hidden"
    :data-tone="tone.tone"
  >
    <div
      class="tone-stripe w-[3px] shrink-0"
      aria-hidden="true"
    />
    <div class="flex min-w-0 flex-1 flex-col gap-3 p-4">
      <div class="flex items-start gap-3">
        <ToneChip
          :tone="tone.tone"
          :glyph="tone.glyph"
          :label="tone.label"
        />
        <h3 class="text-balance font-semibold text-ink">
          {{ finding.title }}
        </h3>
      </div>

      <div>
        <p class="eyebrow">
          Subject
        </p>
        <span class="quoted break-all">{{ finding.subject }}</span>
      </div>

      <p class="max-w-prose break-words text-ink-muted">
        {{ finding.detail }}
      </p>

      <div v-if="finding.risk !== null">
        <p class="eyebrow">
          What this exposes
        </p>
        <p class="break-words text-ink">
          {{ finding.risk }}
        </p>
      </div>

      <div v-if="finding.remediation !== null">
        <p class="eyebrow">
          What to do
        </p>
        <p class="break-words text-ink">
          {{ finding.remediation }}
        </p>
      </div>

      <!-- Evidence is the only thing on a failing finding that collapses. It can
           run to fifty rows and it supports the verdict rather than being it,
           so it starts closed while the remediation above stays open. -->
      <details v-if="finding.evidence.length > 0">
        <summary
          class="tap -ml-1 flex cursor-pointer items-center gap-1.5 rounded-md px-1 text-sm font-medium text-ink-muted select-none hover:text-ink"
        >
          <svg
            class="chevron shrink-0"
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m6 3 5 5-5 5" />
          </svg>
          Evidence ({{ finding.evidence.length }})
        </summary>
        <EvidenceTable :evidence="finding.evidence" />
      </details>

      <footer class="flex flex-col gap-2 text-xs text-ink-faint">
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span class="quoted break-all">{{ finding.checkId }}</span>
          <span v-if="description">{{ description }}</span>
        </div>
        <div v-if="finding.refs.length > 0">
          <p class="eyebrow">
            References
          </p>
          <ul class="flex flex-col gap-1">
            <li
              v-for="(ref, index) in finding.refs"
              :key="index"
            >
              <RefLink :url="ref" />
            </li>
          </ul>
        </div>
      </footer>
    </div>
  </article>
</template>

<style scoped>
/* The default disclosure triangle disappears the moment a summary becomes a
   flex container, and without a marker "Evidence (3)" reads as a heading rather
   than as something to open. */
summary::-webkit-details-marker {
  display: none;
}

.chevron {
  transition: transform var(--dur-micro) ease-out;
}

details[open] .chevron {
  transform: rotate(90deg);
}

@media (prefers-reduced-motion: reduce) {
  .chevron {
    transition: none;
  }
}
</style>
